import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
    Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography,
    IconButton, AppBar, Toolbar, Avatar, Menu, MenuItem, Divider, Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import ThemeConfig from '../../../minimal/theme';
import { PlanProvider } from '../../../contexts/PlanContext';
import AppLoader from '../../AppLoader';
import LocalStorage from '../../LocalStorage';
import i18n, { LANG_STORAGE_KEY } from '../../../i18n';
import { SidebarData } from './SidebarData';
import { can } from '../../../constants/permissions';
import { venueLangCodes } from '../../../constants/languages';
import logo from '../../../assets/brand/cometqr-icon-navy.png';
import usFlag from '../../../assets/flags/us.svg';
import thFlag from '../../../assets/flags/th.svg';

// Real flag graphics (SVG) — emoji flags don't render on Windows (show "US"/"TH" text instead).
const FLAG_SRC = { en: usFlag, th: thFlag };

const DRAWER_WIDTH = 280;
const RAIL_WIDTH = 76; // collapsed sidebar shows a slim icon rail (hover expands it) — never fully hidden

// UI-chrome locales we ship translation files for (i18n/locales/*.json). Menu CONTENT can be any of
// the 13 master languages, but the app UI can only display languages we've authored. The switcher
// shows the intersection of the venue's chosen languages and this list.
const UI_LOCALES = ['en', 'th'];

// One nav row — Minimal styling: rounded, soft active state with a left accent bar.
const navItemSx = {
    minHeight: 46,
    borderRadius: 1,
    mb: 0.5,
    px: 2,
    color: 'text.secondary',
    textTransform: 'capitalize',
    position: 'relative',
    '& .MuiListItemIcon-root': { minWidth: 34, color: 'inherit', fontSize: 20 },
    '&.active': {
        color: 'primary.main',
        fontWeight: 600,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
    },
    '&.active::before': {
        content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
        borderRadius: 4, bgcolor: 'primary.main',
    },
};

function SidebarContent({ nav, authUser, onNav, mini = false }) {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Brand logo moved to the top header bar (Rajat 2026-07-07) so the nav no longer scrolls. */}
            {/* Who's signed in */}
            <Box sx={{ mx: mini ? 1 : 2.5, mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'flex-start', p: 1.5, borderRadius: 1.5, bgcolor: (t) => alpha(t.palette.grey[500], 0.12) }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>{(authUser?.first_name || 'V').charAt(0).toUpperCase()}</Avatar>
                    {!mini && (
                        <Box sx={{ ml: 2, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap>{authUser?.first_name || 'Owner'}</Typography>
                            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>{authUser?.Venue?.title || 'Your venue'}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            <List disablePadding sx={{ px: 1.5, pb: 3 }}>
                {nav.map((item) => (
                    <ListItemButton key={item.path} component={NavLink} to={item.path} onClick={onNav} disableGutters
                        title={mini ? item.label : undefined}
                        sx={{ ...navItemSx, justifyContent: mini ? 'center' : 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: mini ? 0 : 34 }}>{item.icon}</ListItemIcon>
                        {!mini && <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={item.label} />}
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );
}

const OwnerLayout = () => {
    const globalState = useSelector((state) => state.global);
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    // Desktop sidebar collapse — frees the main working zone (Rajat's standing directive #1). Persisted.
    const [collapsed, setCollapsed] = useState(() => {
        // Default to COLLAPSED (rail) so the owner gets maximum working width (Rajat's preference);
        // an explicit choice is remembered thereafter.
        try { const v = localStorage.getItem('mb_sidebar_collapsed'); return v === null ? true : v === '1'; } catch (e) { return true; }
    });
    const [hovered, setHovered] = useState(false); // when collapsed, hovering the rail expands it temporarily
    const railMode = collapsed && !hovered;        // slim icon rail vs full sidebar
    const toggleCollapsed = () => setCollapsed((c) => {
        const next = !c;
        try { localStorage.setItem('mb_sidebar_collapsed', next ? '1' : '0'); } catch (e) { /* ignore */ }
        return next;
    });
    const { i18n: i18nHook } = useTranslation();

    const authUser = globalState.authUser;
    const role = authUser?.role || null;               // null/'owner' → owner; else staff role
    const nav = SidebarData.filter((i) => can(role, i.perm)); // role-aware: staff see a lean menu

    // The admin-UI language switcher offers every locale we ship UI translations for (en/th) — this is
    // the OWNER dashboard's language and is independent of the venue's diner/menu languages. Flags are
    // shown as always-visible chips in the sticky header (Rajat's directive, HitCourt-style).
    const currentLang = (i18nHook.language || 'en').slice(0, 2);
    const changeLang = (code) => {
        i18n.changeLanguage(code);
        try { localStorage.setItem(LANG_STORAGE_KEY, code); } catch (e) { /* ignore */ }
    };

    // Open the app in the VENUE'S DEFAULT language (Settings → Languages, marked "Default" = the first
    // diner_languages code) — but only until the user makes an explicit choice, which we then respect.
    // If we don't ship a UI locale for that default (e.g. Russian), fall back to English, exactly like a
    // dish falling back to the only language it was entered in.
    useEffect(() => {
        let explicit = null;
        try { explicit = localStorage.getItem(LANG_STORAGE_KEY); } catch (e) { /* ignore */ }
        if (explicit) return; // the user has chosen — don't override
        const primary = venueLangCodes(authUser?.Venue?.diner_languages)[0];
        const def = UI_LOCALES.includes(primary) ? primary : 'en';
        if ((i18nHook.language || 'en').slice(0, 2) !== def) i18n.changeLanguage(def);
    }, [authUser?.Venue?.diner_languages, i18nHook.language]);

    const logout = () => { LocalStorage.logout(); window.location.replace('/login'); };

    // Guard: a logged-in session missing its Venue would white-screen child pages — offer a clean relogin.
    if (!authUser?.Venue?.id) {
        return (
            <ThemeConfig>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: '80vh', p: 3 }}>
                    <Typography variant="h5" gutterBottom>Your session needs a refresh</Typography>
                    <Typography sx={{ color: 'text.secondary', maxWidth: 420, mb: 3 }}>We couldn’t load your venue details. Logging in again will fix it — it only takes a moment.</Typography>
                    <Button variant="contained" onClick={logout}>Log in again</Button>
                </Box>
            </ThemeConfig>
        );
    }

    return (
        <PlanProvider>
            <ThemeConfig>
                {globalState.loading ? <AppLoader /> : null}
                <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
                    {/* Desktop: fixed sidebar. Collapsed = slim icon RAIL (never hidden); hovering it
                        expands to full width over the content, then snaps back on mouse-leave. */}
                    <Box
                        onMouseEnter={() => { if (collapsed) setHovered(true); }}
                        onMouseLeave={() => setHovered(false)}
                        sx={{
                            display: { xs: 'none', lg: 'block' },
                            position: 'fixed', top: 0, left: 0, bottom: 0,
                            width: railMode ? RAIL_WIDTH : DRAWER_WIDTH,
                            transition: 'width .2s ease',
                            borderRight: '1px dashed', borderColor: 'divider',
                            bgcolor: 'background.paper', overflow: 'hidden',
                            zIndex: (t) => t.zIndex.appBar + 2, // expanded rail overlays content on hover
                        }}
                    >
                        <SidebarContent nav={nav} authUser={authUser} mini={railMode} />
                    </Box>
                    {/* Mobile: temporary drawer */}
                    <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}>
                        <SidebarContent nav={nav} authUser={authUser} onNav={() => setMobileOpen(false)} />
                    </Drawer>
                    {/* Main column — leaves room for the rail when collapsed (hover-expand overlays, no reflow) */}
                    <Box sx={{ flexGrow: 1, minWidth: 0, ml: { lg: collapsed ? `${RAIL_WIDTH}px` : `${DRAWER_WIDTH}px` }, display: 'flex', flexDirection: 'column' }}>
                        <AppBar position="sticky" elevation={0} sx={{ bgcolor: (t) => alpha(t.palette.background.default, 0.85), backdropFilter: 'blur(6px)', borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
                            <Toolbar>
                                {/* Mobile: open the drawer. Desktop: collapse/expand the sidebar. */}
                                <IconButton onClick={() => setMobileOpen(true)} sx={{ display: { lg: 'none' }, mr: 1, color: 'text.primary' }}><MenuIcon /></IconButton>
                                <IconButton onClick={toggleCollapsed} aria-label="Toggle sidebar" sx={{ display: { xs: 'none', lg: 'inline-flex' }, mr: 1, color: 'text.primary' }}><MenuIcon /></IconButton>
                                {/* Brand — moved here from the sidebar (Rajat 2026-07-07) so the nav no longer scrolls. */}
                                <Box component={NavLink} to="/owner/dashboard" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                                    <Box component="img" src={logo} alt="CometQR" sx={{ height: 32, width: 'auto' }} />
                                    <Typography sx={{ fontWeight: 800, color: '#0d2f4f', fontSize: '1.1rem', letterSpacing: '-0.01em', display: { xs: 'none', sm: 'block' } }}>CometQR</Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1 }} />
                                {/* Language switcher — always-visible flag chips in the sticky header (Rajat's
                                    directive, HitCourt-style). Switches the OWNER dashboard's UI language. */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                                    <LanguageIcon sx={{ color: 'text.disabled', fontSize: 20, mr: 0.25 }} />
                                    {UI_LOCALES.map((code) => {
                                        const active = currentLang === code;
                                        return (
                                            <Box key={code} component="button" type="button" onClick={() => changeLang(code)}
                                                aria-label={`Switch UI to ${code.toUpperCase()}`} title={code.toUpperCase()}
                                                sx={{
                                                    cursor: 'pointer', border: '2px solid', borderRadius: 1, p: '3px 5px',
                                                    lineHeight: 0, background: 'none',
                                                    borderColor: active ? 'primary.main' : 'transparent',
                                                    bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.12) : 'transparent',
                                                    filter: active ? 'none' : 'grayscale(0.55)', opacity: active ? 1 : 0.7,
                                                    transition: 'all .15s ease',
                                                    '&:hover': { opacity: 1, filter: 'none', borderColor: active ? 'primary.main' : 'divider' },
                                                }}>
                                                <Box component="img" src={FLAG_SRC[code]} alt={code.toUpperCase()}
                                                    sx={{ width: 24, height: 17, objectFit: 'cover', borderRadius: '2px', display: 'block' }} />
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>{(authUser?.first_name || 'V').charAt(0).toUpperCase()}</Avatar>
                                </IconButton>
                                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                    <Box sx={{ px: 2, py: 1 }}>
                                        <Typography variant="subtitle2" noWrap>{authUser?.first_name || 'Owner'}</Typography>
                                        <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>{authUser?.email}</Typography>
                                    </Box>
                                    <Divider />
                                    <MenuItem onClick={() => { setAnchorEl(null); navigate('/owner/settings'); }}><SettingsIcon fontSize="small" sx={{ mr: 1.5 }} /> Settings</MenuItem>
                                    <MenuItem onClick={logout} sx={{ color: 'error.main' }}><LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> Logout</MenuItem>
                                </Menu>
                            </Toolbar>
                        </AppBar>
                        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                            <Outlet />
                        </Box>
                    </Box>
                </Box>
            </ThemeConfig>
        </PlanProvider>
    );
};

export default OwnerLayout;
